/**
 * MatterCard - the matter as a designed glass object.
 *
 * Replaces the generic `ListItem` the Card view used to render, which stacked
 * a title, a run-on subtitle and an undifferentiated label/value list, so
 * "Jurisdiction: BG" carried the same visual weight as the matter's name and
 * nothing was scannable at a glance.
 *
 * The hierarchy this reads in, top to bottom:
 *   1. WHO/WHAT   - jurisdiction flag, title, matter type
 *   2. WHERE      - status badge, board stage, due-date urgency
 *   3. FACTS      - a three-fact row: revenue · documents · created
 *   4. PEOPLE     - clients and the collaborator stack, on the footer rail
 *
 * Two densities off ONE component, so the board and the card grid can never
 * drift: `density="card"` is the grid tile, `density="board"` is the narrower
 * tile a stage column holds.
 *
 * A matter that is still processing has no metadata worth showing yet, so it
 * renders as a live progress card instead - never as a card full of dashes.
 */

import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import {
  PiBriefcaseLight,
  PiUserLight,
  PiCalendarBlankLight,
  PiClockLight,
  PiCurrencyDollarLight,
  PiFileTextLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import GlassCard from '../shared/brand/GlassCard';
import Flag from '../shared/TailwindComponents/Flag';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import MiniLoadingToCheckmark from '../shared/animations/MiniLoadingToCheckmark';
import { getStatusColor, formatDate, formatDateTime, parseServerDate } from '../../utils/functions';
import { formatCurrency } from '../../utils/formatCurrency';
import { getJurisdictionDisplay } from '../../utils/jurisdictionFlag';

export interface MatterCardLiveState {
  step?: string;
  message?: string;
  percent?: number;
}

export interface MatterCardProps {
  matter: any;
  onClick: (matter: any) => void;
  live?: MatterCardLiveState;
  /** Grid tile (default) or the narrower tile a board column holds. */
  density?: 'card' | 'board';
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Due-date urgency → card tone. Same three-step ladder the project cards use:
 * overdue is danger, the next week is a warning, everything else is quiet.
 */
const dueTone = (dueDate: string | null | undefined): 'default' | 'warning' | 'danger' => {
  if (!dueDate) return 'default';
  const due = parseServerDate(dueDate);
  if (!due) return 'default';
  const days = (due.getTime() - Date.now()) / 86_400_000;
  if (days < 0) return 'danger';
  if (days <= 7) return 'warning';
  return 'default';
};

/** One fact on the card's fact rail: muted glyph, muted label, solid value. */
const CardFact: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode }> = ({
  icon,
  label,
  value,
}) => (
  <div className="min-w-0">
    <div className="flex items-center gap-1 text-[11px] font-medium text-gray-400 dark:text-gray-500">
      <span className="[&>svg]:h-3.5 [&>svg]:w-3.5">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-0.5 text-sm font-medium text-gray-700 dark:text-gray-200 truncate tabular-nums">
      {value}
    </div>
  </div>
);

const MatterCard: React.FC<MatterCardProps> = ({
  matter,
  onClick,
  live,
  density = 'card',
  className,
  style,
}) => {
  const { t } = useTranslation();

  const processingStatus = matter.processing_status || 'pending';
  const isCompleted = processingStatus === 'completed' || processingStatus === 'open';
  const isFailed = processingStatus === 'failed';
  const isProcessing = processingStatus === 'processing' || processingStatus === 'running';

  const title = matter.title || matter.MatterName || `${t('Matter')} ${matter.MatterID}`;
  const type = matter.type || '';
  const jurisdiction = getJurisdictionDisplay(matter.jurisdiction);
  const dueDate = matter.FinancialData?.DueDate || matter.dueDate || null;
  const tone = isCompleted ? dueTone(dueDate) : 'default';

  const clients: any[] = matter.matter_clients?.clients || [];
  const documentCount = matter.DocumentCount || 0;

  const createdAt = matter.created_at
    ? (Date.now() - new Date(matter.created_at).getTime()) / 3_600_000
    : Number.POSITIVE_INFINITY;
  const isNew = createdAt < 24;

  /*
    Revenue: `null` means the backend could not price the matter - unknown,
    not zero - so it shows as a dash. A real 0.0 is a fact and formats as
    money like any other figure.
  */
  const revenue: number | null = matter.revenue ?? null;
  const revenueText =
    revenue === null
      ? '—'
      : formatCurrency(revenue, matter.revenue_currency || 'usd');

  const compact = density === 'board';

  // ── Still cooking: show the pipeline, not an empty skeleton of facts ──
  if (!isCompleted) {
    return (
      <GlassCard
        padding={compact ? 'sm' : 'md'}
        className={clsx('opacity-80', className)}
        style={style}
      >
        <div className="flex items-center gap-2">
          {isFailed ? (
            <PiWarningCircleLight className="h-4 w-4 flex-shrink-0 text-danger-500" />
          ) : isProcessing ? (
            <MiniLoadingToCheckmark isLoading />
          ) : (
            <PiClockLight className="h-4 w-4 flex-shrink-0 text-warning-400 animate-pulse" />
          )}
          <span
            className={clsx(
              'text-sm truncate',
              isFailed ? 'text-danger-500 dark:text-danger-400' : 'text-fg-muted italic'
            )}
          >
            {isFailed
              ? t('Processing failed')
              : live?.step
                ? live.step.replace(/_/g, ' ')
                : isProcessing
                  ? t('Processing matter...')
                  : processingStatus === 'queued'
                    ? t('Queued for processing...')
                    : t('Waiting to be queued...')}
          </span>
        </div>
        {live?.message && (
          <p className="mt-1.5 text-xs text-fg-muted truncate">{live.message}</p>
        )}
        {live?.percent != null && live.percent > 0 && (
          <div className="mt-2 h-1 w-full bg-surface-sunken rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-info-500 to-info-700 rounded-full transition-all duration-700 ease-in-out"
              style={{ width: `${live.percent}%` }}
            />
          </div>
        )}
        {!isFailed && !live?.message && (
          <p className="mt-1.5 text-xs text-fg-muted">
            {t('Details will appear once processing completes')}
          </p>
        )}
      </GlassCard>
    );
  }

  return (
    <GlassCard
      padding="none"
      tone={tone}
      className={clsx(
        'group h-full overflow-hidden cursor-pointer',
        'transition-transform duration-200 ease-slide hover:-translate-y-0.5 hover:shadow-md',
        className
      )}
      style={style}
      bodyClassName="flex flex-col h-full"
    >
      <div
        role="link"
        tabIndex={0}
        onClick={() => onClick(matter)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick(matter);
          }
        }}
        className={clsx(
          'flex flex-col h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60 rounded-lg',
          compact ? 'p-3 gap-2' : 'p-4 gap-3'
        )}
      >
        {/* ── 1 · identity ── */}
        <div className="flex items-start gap-2 min-w-0">
          <span className="mt-0.5 flex-shrink-0">
            {jurisdiction ? (
              <Flag countryCode={jurisdiction.countryCode} code={jurisdiction.code} size="sm" />
            ) : (
              <PiBriefcaseLight className="h-4 w-4 text-fg-muted" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <h3
              className={clsx(
                'font-medium text-fg-strong leading-snug line-clamp-2 group-hover:text-champagne transition-colors duration-200',
                compact ? 'text-[13px]' : 'text-sm'
              )}
              title={title}
            >
              {title}
            </h3>
            {type && (
              <p className="mt-0.5 text-[11px] text-fg-muted truncate">{type}</p>
            )}
          </div>
          {/* Status sits top-right of the card (UI feedback M-4). */}
          <div className="flex-shrink-0 flex items-center gap-1.5">
            {isNew && <Badge color="green">{t('New')}</Badge>}
            <Badge className="capitalize" color={getStatusColor(matter.status_name) as BadgeColor}>
              {matter.status_name || t('No status')}
            </Badge>
          </div>
        </div>

        {/* ── 2 · where it stands ── */}
        <div className="flex items-center gap-1.5 flex-wrap empty:hidden">
          {matter.board_stage && density === 'card' && (
            <span className="inline-flex items-center gap-1 rounded-full border border-hairline-soft px-2 py-0.5 text-[10px] font-medium text-fg-muted">
              <span className="h-1 w-1 rounded-full bg-champagne" aria-hidden />
              {t(matter.board_stage)}
            </span>
          )}
          {dueDate && (
            <span
              className={clsx(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium',
                tone === 'danger'
                  ? 'bg-danger-50 dark:bg-danger-900/30 text-danger-600 dark:text-danger-300'
                  : tone === 'warning'
                    ? 'bg-warning-50 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300'
                    : 'text-fg-muted'
              )}
            >
              <PiCalendarBlankLight className="h-3 w-3" />
              {formatDateTime(dueDate)}
            </span>
          )}
        </div>

        {/* ── 3 · the facts, in a fixed rail so cards line up with each other ── */}
        {!compact && (
          <div className="grid grid-cols-3 gap-2 rounded-md bg-surface-sunken/70 border border-hairline-soft px-3 py-2">
            <CardFact
              icon={<PiCurrencyDollarLight />}
              label={t('Revenue')}
              value={
                revenue === null
                  ? <span className="text-gray-400 dark:text-gray-500">{revenueText}</span>
                  : revenueText
              }
            />
            <CardFact
              icon={<PiFileTextLight />}
              label={t('Documents')}
              value={documentCount}
            />
            <CardFact
              icon={<PiCalendarBlankLight />}
              label={t('Created')}
              value={formatDate(matter.created_at)}
            />
          </div>
        )}

        {compact && revenue !== null && (
          <div className="flex items-center gap-1 text-[11px] text-fg-muted tabular-nums">
            <PiCurrencyDollarLight className="h-3.5 w-3.5" />
            {revenueText}
          </div>
        )}

        {/* ── 4 · people, on the footer rail ── */}
        <div className="mt-auto pt-2 border-t border-hairline-soft flex items-center justify-between gap-2 min-w-0">
          <span className="flex items-center gap-1 text-[11px] text-fg-body truncate min-w-0">
            <PiUserLight className="h-3.5 w-3.5 flex-shrink-0 text-fg-muted" />
            {clients.length > 0
              ? clients.length === 1
                ? clients[0].name || t('Unknown Contact')
                : t('{{count}} contacts', { count: clients.length })
              : t('No contact')}
          </span>
          <CollaboratorStack
            collaborators={matter.Collaborators || []}
            max={compact ? 2 : 3}
            size="xs"
            withAccessLabels={false}
          />
        </div>
      </div>
    </GlassCard>
  );
};

export default MatterCard;
